/** @jsx jsx */
/* eslint-disable jsx-a11y/label-has-associated-control, max-len */
import React, { useEffect, useState } from 'react';
import { css, jsx } from '@emotion/core';
import Modal from 'components/molecules/Modal';
import { getRequiredStaff, saveRequiredStaffDaily, saveRequiredStaffTemplate } from 'api/shiftBoard';
import {
  BoardData, BreakdownAxis, RequiredStaffRow,
} from 'domain/attend/shiftBoard/types';
import { DOW_LABELS, parseDateStr, shortDateLabel } from 'domain/attend/shiftBoard/time';
import { boardStyles, mock } from './styles';
import BoardSelect from './BoardSelect';

export type RequiredStaffPanelProps = {
  open: boolean;
  board: BoardData;
  dateFrom: string;
  dateTo: string;
  editable: boolean;
  onClose: () => void;
  onSaved: () => void;
};

// 8 = 祝日の前日、9 = 祝日。祝日 > 祝日の前日 > 曜日 の順で使われる（9/9 決定）
const DOWS = [1, 2, 3, 4, 5, 6, 0, 9, 8];

const dowLabel = (d: number): string => {
  if (d === 9) {
    return '祝日';
  }
  return d === 8 ? '祝日の前日' : `${DOW_LABELS[d]}曜日`;
};

/** 見出しの色。日曜と祝日は赤、土曜は青 */
const dowColor = (d: number): string => {
  if (d === 0 || d === 9) {
    return mock.secondary;
  }
  return d === 6 ? mock.primary : mock.textMain;
};

const styles = {
  sectionTitle: css({
    fontSize: '13px', fontWeight: 700, color: mock.textMain, margin: '0 0 4px',
  }),
  sectionNote: css({
    fontSize: '11px', color: mock.textSub, lineHeight: 1.6, margin: '0 0 12px',
  }),
  tiles: css({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))',
    gap: '10px',
    marginBottom: '16px',
  }),
  tile: css({
    border: `1px solid ${mock.gray10}`,
    borderRadius: '4px',
    padding: '10px 8px',
    background: '#fff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
  }),
  tileLabel: css({ fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' }),
  tileInput: css({
    width: '72px',
    height: '34px',
    border: `1px solid ${mock.gray20}`,
    borderRadius: '4px',
    fontSize: '14px',
    textAlign: 'center',
    fontFamily: 'inherit',
    color: mock.textMain,
    '&:disabled': { background: '#F5F5F5', color: mock.gray40 },
  }),
  divider: css({ border: 'none', borderTop: `1px solid ${mock.gray10}`, margin: '4px 0 16px' }),
  compare: css({
    display: 'flex',
    alignItems: 'flex-end',
    gap: '14px',
    flexWrap: 'wrap',
    border: `1px solid ${mock.gray10}`,
    borderRadius: '4px',
    padding: '14px 16px',
    background: '#fff',
    marginBottom: '12px',
  }),
  field: css({ display: 'flex', flexDirection: 'column', gap: '6px' }),
  fieldLabel: css({ fontSize: '12px', fontWeight: 700, color: mock.textMain }),
  readonly: css({
    height: '34px',
    minWidth: '72px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${mock.gray10}`,
    borderRadius: '4px',
    background: '#F5F5F5',
    color: mock.textSub,
    fontSize: '13px',
  }),
  // 行は flex-end で並ぶ。ラベルを含めた高さの中央ではなく、入力欄の中央にそろえる
  toArrow: css({
    alignSelf: 'flex-end',
    height: '34px',
    display: 'inline-flex',
    alignItems: 'center',
    color: mock.gray40,
    fontSize: '13px',
  }),
  overrideList: css({
    border: `1px solid ${mock.gray10}`, borderRadius: '4px', maxHeight: '150px', overflowY: 'auto',
  }),
  overrideRow: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '6px 12px',
    fontSize: '12px',
    borderBottom: `1px solid ${mock.gray10}`,
    '&:last-of-type': { borderBottom: 'none' },
  }),
  empty: css({ padding: '12px', fontSize: '12px', color: mock.textSub }),
};

/**
 * 予定人数（曜日ごとの合計、特定日の上書き）
 *
 * 9/18 にレイアウトを組み直した。曜日は 9 つのタイル、特定日は
 * 「曜日の設定 → この日だけの人数」を並べて、何がどう変わるのかを読めるようにしている。
 */
const RequiredStaffPanel: React.FC<RequiredStaffPanelProps> = ({
  open, board, dateFrom, dateTo, editable, onClose, onSaved,
}) => {
  const [axis, setAxis] = useState<BreakdownAxis>(board.setting.breakdownAxis);
  const [template, setTemplate] = useState<{ [k: string]: number }>({});
  const [dailyDate, setDailyDate] = useState<string>(dateFrom);
  const [daily, setDaily] = useState<{ [k: string]: number }>({});
  const [tab, setTab] = useState<'template' | 'daily'>('template');
  const [error, setError] = useState<string | null>(null);

  // 業務別・パターン別の内訳は当面表示しない（2026-09-09 打合せ）。
  // T1.breakdown_axis と A14 のリクエストは残し、保存時は取得した軸をそのまま返す。

  useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
    getRequiredStaff(board.org.orgCode, dateFrom, dateTo).then((d) => {
      setAxis(d.breakdownAxis);
      // 内訳（業務別・パターン別）の行は使わない。合計の行だけ拾う
      const t: { [k: string]: number } = {};
      d.template.forEach((r) => {
        if (!r.key) {
          t[`${r.dow}|`] = r.required;
        }
      });
      setTemplate(t);
      const dd: { [k: string]: number } = {};
      d.daily.forEach((r) => {
        if (!r.key) {
          dd[`${r.targetDate}|`] = r.required;
        }
      });
      setDaily(dd);
    }).catch(() => setError('取得できませんでした。'));
  }, [open, board.org.orgCode, dateFrom, dateTo]);

  const submit = async () => {
    setError(null);
    try {
      if (tab === 'template') {
        const rows: Array<RequiredStaffRow> = [];
        Object.keys(template).forEach((k) => {
          const [dow] = k.split('|');
          if (template[k] > 0) {
            rows.push({ dow: parseInt(dow, 10), key: null, required: template[k] });
          }
        });
        await saveRequiredStaffTemplate(board.org.orgCode, axis, rows);
      } else {
        const value = daily[`${dailyDate}|`];
        const rows = value > 0 ? [{ key: null, required: value }] : [];
        await saveRequiredStaffDaily(board.org.orgCode, dailyDate, rows);
      }
      onSaved();
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.message) || '保存できませんでした。');
    }
  };

  // 選んでいる日に対して、曜日の設定から決まる人数。
  // サーバの予定人数と同じ順（祝日 → 祝日の前日 → 曜日。設定の無い区分は飛ばす）で決める（10/5）
  const selectedDow = dailyDate ? parseDateStr(dailyDate).getDay() : 0;
  const selectedDay = board.days.filter((d) => d.date === dailyDate)[0];
  const isHoliday = !!(selectedDay && selectedDay.publicHolidayName);
  const isHolidayEve = !!(selectedDay && selectedDay.holidayEve);
  // 入力を空にすると 0 が入り、保存では 0 の行を送らない。保存したときと同じく 0 は「設定なし」として扱う
  const hasTemplate = (d: number) => (template[`${d}|`] || 0) > 0;
  let baseDow = selectedDow;
  if (isHoliday && hasTemplate(9)) {
    baseDow = 9;
  } else if (isHolidayEve && hasTemplate(8)) {
    baseDow = 8;
  }
  const baseValue = template[`${baseDow}|`];
  const overrideValue = daily[`${dailyDate}|`];

  // この期間で上書きしている日
  const overrides = board.days
    .filter((d) => daily[`${d.date}|`] > 0)
    .map((d) => ({ date: d.date, required: daily[`${d.date}|`] }));

  return (
    <Modal
      open={open}
      closeHandler={onClose}
      title="予定人数"
      submitText={tab === 'template' ? '曜日ごとの設定を保存' : 'この日の設定を保存'}
      onSubmit={submit}
      // 閲覧のみの人も「キャンセル」で閉じられるようにする（10/5）
      isDisabledSubmit={!editable}
      width="720px"
    >
      <div css={boardStyles.modalBody}>
        <div css={boardStyles.segment} style={{ marginBottom: '16px' }}>
          <button type="button" data-active={tab === 'template'} onClick={() => setTab('template')}>曜日ごと</button>
          <button type="button" data-active={tab === 'daily'} onClick={() => setTab('daily')}>特定日の上書き</button>
        </div>
        {error && <div css={boardStyles.dangerText} style={{ marginBottom: '12px' }}>{error}</div>}

        {tab === 'template' && (
          <React.Fragment>
            <div css={styles.sectionTitle}>曜日ごとの予定人数</div>
            <div css={styles.sectionNote}>
              空欄の曜日は予定人数を持たず、人数の過不足も判定しません。
              祝日の前日が土日と重なった日は、祝日の前日の人数を使います。
            </div>
            <div css={styles.tiles}>
              {DOWS.map((dow) => (
                <div key={dow} css={styles.tile}>
                  <span css={styles.tileLabel} style={{ color: dowColor(dow) }}>{dowLabel(dow)}</span>
                  <input
                    css={styles.tileInput}
                    type="number"
                    min={0}
                    max={99}
                    placeholder="—"
                    aria-label={`${dowLabel(dow)}の予定人数`}
                    value={template[`${dow}|`] === undefined ? '' : template[`${dow}|`]}
                    onChange={(e) => setTemplate({ ...template, [`${dow}|`]: parseInt(e.target.value || '0', 10) })}
                    disabled={!editable}
                  />
                </div>
              ))}
            </div>
          </React.Fragment>
        )}

        {tab === 'daily' && (
          <React.Fragment>
            <div css={styles.sectionTitle}>特定日の上書き</div>
            <div css={styles.sectionNote}>
              曜日ごとの設定より、この日の人数を優先します。0 か空欄で保存すると上書きを解除します。
            </div>
            <div css={styles.compare}>
              <div css={styles.field}>
                <label css={styles.fieldLabel} htmlFor="rs-date">日付</label>
                {/* ほかの画面と同じ共通の Select（10/7） */}
                <BoardSelect
                  id="rs-date"
                  value={dailyDate}
                  onChange={setDailyDate}
                  options={board.days.map((d) => ({
                    value: d.date,
                    label: `${shortDateLabel(d.date)}${d.publicHolidayName ? `（${d.publicHolidayName}）` : ''}`,
                  }))}
                  style={{ minWidth: '180px' }}
                />
              </div>
              <div css={styles.field}>
                <span css={styles.fieldLabel}>{`${dowLabel(baseDow)}の設定`}</span>
                <span css={styles.readonly}>{baseValue === undefined || baseValue === 0 ? '未設定' : `${baseValue}人`}</span>
              </div>
              <span css={styles.toArrow}>❯</span>
              <div css={styles.field}>
                <label css={styles.fieldLabel} htmlFor="rs-value">この日の人数</label>
                <input
                  id="rs-value"
                  css={styles.tileInput}
                  type="number"
                  min={0}
                  max={99}
                  placeholder="—"
                  value={overrideValue === undefined ? '' : overrideValue}
                  onChange={(e) => setDaily({ ...daily, [`${dailyDate}|`]: parseInt(e.target.value || '0', 10) })}
                  disabled={!editable}
                />
              </div>
            </div>

            <hr css={styles.divider} />
            <div css={styles.sectionTitle}>この期間で上書きしている日</div>
            <div css={styles.sectionNote}>保存すると一覧に反映されます。</div>
            <div css={styles.overrideList}>
              {overrides.map((o) => (
                <div key={o.date} css={styles.overrideRow}>
                  <span>{shortDateLabel(o.date)}</span>
                  <span style={{ fontWeight: 700 }}>{`${o.required}人`}</span>
                </div>
              ))}
              {overrides.length === 0 && <div css={styles.empty}>上書きしている日はありません。</div>}
            </div>
          </React.Fragment>
        )}
      </div>
    </Modal>
  );
};

export default RequiredStaffPanel;
